import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import StructuredData from '../components/StructuredData'
import './SpecialtyCabinetsLandingPage.css'

interface QuoteFormData {
  name: string
  email: string
  phone: string
  company?: string
  referral?: string
  projectType: string
}

function SpecialtyCabinetsLandingPage() {
  const formRef = useRef<HTMLDivElement>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitError, setIsSubmitError] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<QuoteFormData>()

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mojnjjob'

  const onSubmit = async (data: QuoteFormData) => {
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company || '',
          referral: data.referral || '',
          projectType: data.projectType,
          _subject: 'Specialty Cabinets Quote Request',
        }),
      })
      if (!res.ok) throw new Error('Submit failed')
      setIsSubmitted(true)
      reset()
      setTimeout(() => setIsSubmitted(false), 8000)
    } catch {
      setIsSubmitError(true)
      setTimeout(() => setIsSubmitError(false), 5000)
    }
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Laboratory Specialty Safety Cabinets',
    description:
      'NFPA and OSHA compliant safety storage cabinets for flammable, corrosive, toxic, and hazardous chemicals. Designed to meet stringent safety and regulatory requirements.',
    brand: { '@type': 'Brand', name: 'Laboratory Specialists' },
    offers: { '@type': 'Offer', priceCurrency: 'USD', availability: 'https://schema.org/UponRequest' },
  }

  return (
    <div className="specialty-cabinets-landing-page">
      <StructuredData data={structuredData} />

      {/* Hero: headline + sub-headline + proof + CTA */}
      <section className="specialty-cabinets-hero">
        <div className="specialty-cabinets-hero-bg" aria-hidden />
        <div className="container specialty-cabinets-hero-inner">
          <h1 className="specialty-cabinets-hero-title">
            Specialty Safety Cabinets That Meet NFPA and OSHA Standards
          </h1>
          <p className="specialty-cabinets-hero-subtitle">
            Compliant storage solutions for flammable, corrosive, toxic, and hazardous chemicals. We help you select, configure, and install safety cabinets that protect your team and meet regulatory requirements.
          </p>
          <div className="specialty-cabinets-hero-cta">
            <button type="button" className="specialty-cabinets-cta-button" onClick={scrollToForm}>
              Get Your Free Quote
            </button>
            <p className="specialty-cabinets-hero-cta-note">Custom quote in 24 hours · No obligation</p>
          </div>
        </div>
      </section>

      {/* Three objections (below the fold) */}
      <section className="specialty-cabinets-objections">
        <div className="container">
          <ul className="specialty-cabinets-bullets">
            <li>
              <strong>Not sure which cabinet type you need?</strong> We'll help you identify the right cabinet based on your chemicals, storage requirements, and compliance needs—whether it's flammable, corrosive, toxic, or oxidizer storage.
            </li>
            <li>
              <strong>Concerned about NFPA 30 and OSHA compliance?</strong> All our cabinets meet or exceed NFPA, OSHA, and institutional safety regulations, with proper construction, venting, labeling, and safety features built in.
            </li>
            <li>
              <strong>Need complete installation and safety guidance?</strong> We handle cabinet selection, delivery, installation, labeling, and provide guidance on proper use, inspection, and compliance—one partner for the entire process.
            </li>
          </ul>
        </div>
      </section>

      {/* Quote form - primary conversion */}
      <section className="specialty-cabinets-form-section" ref={formRef} id="quote-form">
        <div className="container">
          <div className="specialty-cabinets-form-card">
            <h2 className="specialty-cabinets-form-title">Get Your Custom Quote</h2>
            <p className="specialty-cabinets-form-lead-magnet">
              Get a custom quote for safety storage cabinets—no obligation.
            </p>

            {isSubmitted ? (
              <div className="specialty-cabinets-form-success">
                <h3>Thanks—we'll be in touch within 24 hours.</h3>
                <p>We'll email or call with your quote and next steps.</p>
              </div>
            ) : (
              <form className="specialty-cabinets-form" onSubmit={handleSubmit(onSubmit)}>
                {isSubmitError && (
                  <p className="specialty-cabinets-form-error" role="alert">
                    Something went wrong. Please try again or call us at (541) 658-9374.
                  </p>
                )}
                <div className="specialty-cabinets-form-group">
                  <label htmlFor="specialty-cabinets-name">
                    Full Name <span className="specialty-cabinets-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="specialty-cabinets-name"
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Please enter your full name' },
                    })}
                    aria-invalid={errors.name ? 'true' : 'false'}
                    placeholder="First and Last Name"
                  />
                  {errors.name && (
                    <span className="specialty-cabinets-error" role="alert">
                      {errors.name.message}
                    </span>
                  )}
                </div>

                <div className="specialty-cabinets-form-group">
                  <label htmlFor="specialty-cabinets-email">
                    Email <span className="specialty-cabinets-required">*</span>
                  </label>
                  <input
                    type="email"
                    id="specialty-cabinets-email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email',
                      },
                    })}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    placeholder="Email Address"
                  />
                  {errors.email && (
                    <span className="specialty-cabinets-error" role="alert">
                      {errors.email.message}
                    </span>
                  )}
                </div>

                <div className="specialty-cabinets-form-group">
                  <label htmlFor="specialty-cabinets-phone">
                    Phone <span className="specialty-cabinets-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="specialty-cabinets-phone"
                    {...register('phone', {
                      required: 'Phone is required',
                      pattern: {
                        value: /^[\d\s\-+()]+$/,
                        message: 'Please enter a valid phone number',
                      },
                    })}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                    placeholder="123-456-7890"
                  />
                  {errors.phone && (
                    <span className="specialty-cabinets-error" role="alert">
                      {errors.phone.message}
                    </span>
                  )}
                </div>

                <div className="specialty-cabinets-form-group">
                  <label htmlFor="specialty-cabinets-company">Company / Institution (optional)</label>
                  <input
                    type="text"
                    id="specialty-cabinets-company"
                    {...register('company')}
                    placeholder="Your lab or company name"
                  />
                </div>

                <div className="specialty-cabinets-form-group">
                  <label htmlFor="specialty-cabinets-referral">How did you hear about us? (optional)</label>
                  <input
                    type="text"
                    id="specialty-cabinets-referral"
                    {...register('referral')}
                    placeholder="e.g. Google, referral, trade show..."
                  />
                </div>

                <div className="specialty-cabinets-form-group">
                  <label htmlFor="specialty-cabinets-projectType">
                    What do you need? <span className="specialty-cabinets-required">*</span>
                  </label>
                  <select
                    id="specialty-cabinets-projectType"
                    {...register('projectType', { required: 'Please select an option' })}
                    aria-invalid={errors.projectType ? 'true' : 'false'}
                  >
                    <option value="">Select...</option>
                    <option value="flammable-storage">Flammable Liquid Storage Cabinet (yellow)</option>
                    <option value="acid-corrosive-storage">Acid & Corrosive Storage Cabinet (blue)</option>
                    <option value="toxic-poisonous-storage">Toxic & Poisonous Chemical Cabinet (white/gray)</option>
                    <option value="oxidizer-storage">Oxidizer Storage Cabinet</option>
                    <option value="pesticide-storage">Pesticide & Agricultural Chemical Cabinet (green)</option>
                    <option value="combination-storage">Combination Storage Cabinet (multi-compartment)</option>
                    <option value="ventilated-storage">Ventilated Safety Cabinet (ducted/ductless)</option>
                    <option value="multiple-cabinets">Multiple cabinet types needed</option>
                    <option value="not-sure">Not sure yet—need guidance</option>
                  </select>
                  {errors.projectType && (
                    <span className="specialty-cabinets-error" role="alert">
                      {errors.projectType.message}
                    </span>
                  )}
                </div>

                <p className="specialty-cabinets-privacy">
                  By submitting you agree to our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  . We won't share your information.
                </p>

                <button type="submit" className="specialty-cabinets-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Get My Quote'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="specialty-cabinets-legal">
        <div className="container">
          <p className="specialty-cabinets-legal-text">
            Laboratory Specialists. Specialty safety cabinets for hazardous chemical storage. NFPA 30 and OSHA 1910.106 compliant.{' '}
            <a href="/privacy">Privacy Policy</a> · <a href="/contact">Contact</a>
          </p>
        </div>
      </section>
    </div>
  )
}

export default SpecialtyCabinetsLandingPage
